import { Tilasana } from "./Tila";
import { useCallback, useEffect, useState } from "react";
import { useKohde } from "./Kohdekonteksti";
import { naytaPvm } from "./pvm";
import { useTaulukko } from "./taulukko";
import { nayttonimi, nayttonimiPieni, selite, vaihtoehdot } from "./nayttonimi";
import {
  api, ApiError,
  type Kayttaja, type Kalusto as KalustoRivi, type KalustoTarkastus, type KalustoTyyppi,
  type KalustoLaji, type KalustoSijaintilaji, type KalustoSyote, type Kohde,
} from "./api";
import Liitteet from "./Liitteet";
import PohjaTuontiPaneeli from "./PohjaTuonti";
import Tarrat from "./Tarrat";
import { Ohje } from "./Ohje";
import { t, virheTeksti, type KaannosAvain } from "./kieli";

/* ═══════════════════════════════════════════════════════════
   Kalusto.

   164 (Toni 21.–23.9.2026, Markku 23.9. «Tärkeintä olisi nyt se
   laiteluettelo»): yksi rekisteri kolmella lajilla Pro-Totin
   inventaariopohjan mukaan — työkalut, nostoapuvälineet ja
   henkilövaljaat. Tyyppilista on ylläpidettävä («Muu» → uusi tyyppi),
   sijainti on varasto, kohde, auto tai toimipiste, ja ylläpito on
   kalustovastaavan (koko yritys tai kohde), ei roolin.

   Katsastus kuten ennen: todistus, voimassaolo, muistutus. Väri
   Pro-Totin pohjan mukaan: punainen myöhässä, keltainen 30 pv sisällä.
   ═══════════════════════════════════════════════════════════ */

/* Nimi avaimena eikä tekstinä: moduulin tasolla t() jäädyttäisi kielen lataushetkeen. */
const LAJIT: { arvo: KalustoLaji; avain: KaannosAvain }[] = [
  { arvo: "tyokalu", avain: "kal.laji_tyokalu" },
  { arvo: "nostoapuvaline", avain: "kal.laji_nosto" },
  { arvo: "henkilovaljaat", avain: "kal.laji_valjaat" },
];
const MUU = "__muu__";

/** Sijainti yhdeksi tekstiksi — sama muoto kuin Excelissä. */
export function sijaintiTeksti(k: Pick<KalustoRivi, "sijainti_laji" | "sijainti_kohde" | "sijainti_teksti">): string {
  if (!k.sijainti_laji) return "";
  const nimi = nayttonimi("kalusto_sijainti", k.sijainti_laji);
  const loppu = k.sijainti_laji === "kohde" ? k.sijainti_kohde
    : k.sijainti_laji === "henkilo" ? (k as { sijainti_henkilo?: string | null }).sijainti_henkilo ?? null
    : k.sijainti_teksti;
  return loppu ? `${nimi}: ${loppu}` : nimi;
}

/* 54 (Toni 5.10.2026): tulostettava koetarra-arkki — #/kalusto?tarrat=<alku>-<loppu>. Parametri luetaan osoitteesta
   suoraan, koska se ei ole hakua (osoiteHaku) eikä valikkokohta; arkki ei kirjoita mitään. Oma kääre eikä aikainen
   return listakomponentissa: hookit eivät saa olla ehdollisia (rules-of-hooks kaatoi käännöksen 5.10.). */
export default function Kalusto(p: { kayttaja: Kayttaja; osoiteHaku?: string; asetaOsoiteHaku?: (h: string) => void }) {
  const tarrat = new URLSearchParams(window.location.hash.split("?")[1] ?? "").get("tarrat");
  if (tarrat) return <Tarrat kayttaja={p.kayttaja} alue={tarrat} />;
  return <KalustoLista {...p} />;
}
function KalustoLista({ kayttaja, osoiteHaku, asetaOsoiteHaku }: { kayttaja: Kayttaja; osoiteHaku?: string; asetaOsoiteHaku?: (h: string) => void }) {
  // 164: ylläpito on kalustovastaavan — koko yritys tai kohde (163).
  const koko = kayttaja.oikeudet.includes("kalustovastaava");
  const omatKohteet = (kayttaja.kohdeOikeudet?.["kalustovastaava"] ?? []).map(Number);
  const saaLuoda = koko || omatKohteet.length > 0;
  const saaRivia = (k: KalustoRivi) => koko || (k.sijainti_kohde_id != null && omatKohteet.includes(Number(k.sijainti_kohde_id)));

  const [lista, setLista] = useState<KalustoRivi[] | null>(null);
  const [tyypit, setTyypit] = useState<KalustoTyyppi[]>([]);
  const [kohteet, setKohteet] = useState<Kohde[]>([]);
  const [virhe, setVirhe] = useState<string | null>(null);
  const [avattu, setAvattu] = useState<number | null>(null);
  const [uusiAuki, setUusiAuki] = useState(false);
  const [naytaPoistetut, setNaytaPoistetut] = useState(false);
  const [laji, setLaji] = useState<KalustoLaji | "">("");
  const [tyyppilistaAuki, setTyyppilistaAuki] = useState(false);
  /* 74 (Toni 5.10.2026 klo 11.25): joukkohyväksyntä. Valinta vain odottavilla riveillä, joihin on oikeus; vahvistusrivi
     sivulla (ei selaimen confirm); yksi peruste koskee kaikkia hylättyjä. */
  const [vainOdottavat, setVainOdottavat] = useState(false);
  const [valitut, setValitut] = useState<number[]>([]);
  const [joukkoVahvistus, setJoukkoVahvistus] = useState<"hyvaksytty" | "hylatty" | null>(null);
  const [joukkoPeruste, setJoukkoPeruste] = useState("");
  const [joukkoVirhe, setJoukkoVirhe] = useState<string | null>(null);
  const [joukkoIlmoitus, setJoukkoIlmoitus] = useState<string | null>(null);
  const [joukkoKesken, setJoukkoKesken] = useState(false);

  // A3–A7: jaettu lajittelu ja haku (taulukko.tsx). Katsastuksen tila lajittuu
  // kiireellisyysjärjestyksessä: ei tietoa · vanhentunut · vanhenee · voimassa.
  // Toni 10.9.: «ei tietoa» on oma tila listan ALUSSA.
  const TILAJARJESTYS: Record<string, number> = { pois_kaytosta: -1, ei_katsastusta: 0, vanhentunut: 1, vanhenee: 2, voimassa: 3 };
  const nakyvat = lista?.filter((k) => (!laji || k.laji === laji) && (!vainOdottavat || k.hyvaksynta === "ehdotettu")) ?? null;
  const odottavat = (lista ?? []).filter((k) => k.hyvaksynta === "ehdotettu" && !k.poistettu && saaRivia(k));
  const valittavissa = (k: KalustoRivi) => k.hyvaksynta === "ehdotettu" && !k.poistettu && saaRivia(k);
  const vaihdaValinta = (id: number) => setValitut((v) => v.includes(id) ? v.filter((x) => x !== id) : [...v, id]);
  const valitutRivit = (lista ?? []).filter((k) => valitut.includes(k.id) && valittavissa(k));
  async function joukkoPaata() {
    if (!joukkoVahvistus || !valitutRivit.length) return;
    setJoukkoKesken(true); setJoukkoVirhe(null); setJoukkoIlmoitus(null);
    try {
      const r = await api.kalustoHyvaksyntaJoukko(valitutRivit.map((k) => k.id), joukkoVahvistus, joukkoVahvistus === "hylatty" ? joukkoPeruste.trim() : undefined);
      setJoukkoIlmoitus(t(joukkoVahvistus === "hyvaksytty" ? "kal.joukko_hyvaksytty" : "kal.joukko_hylatty", { n: r.maara, tunnukset: r.tunnukset.join(", ") }));
      setValitut([]); setJoukkoVahvistus(null); setJoukkoPeruste("");
      await muuttui();
    } catch (e) { setJoukkoVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("kal.paatos_virhe")); }
    finally { setJoukkoKesken(false); }
  }
  const tb = useTaulukko(nakyvat, {
    avaimet: {
      // 180: K-2 ennen K-10:tä — numero täytetään vertailua varten, ei näytölle.
      tunnus: (k) => k.tunnus.replace(/\d+/g, (d) => d.padStart(12, "0")), nimi: (k) => k.nimi, tyyppi: (k) => k.tyyppi ?? "",
      sijainti: (k) => sijaintiTeksti(k),
      tila: (k) => TILAJARJESTYS[k.tila] ?? 9, katsastus: (k) => k.katsastus_asti,
    },
    // Korjaus b (Toni 28.9.2026): tarran numero haettavissa — naarmuuntuneen QR:n «17» löytää laitteen.
    hakukentat: (k) => [k.tunnus, k.tarra != null ? `Tarra ${k.tarra}` : null, k.nimi, k.tyyppi, sijaintiTeksti(k), k.valmistaja, k.malli, k.sarjanumero, k.kantavuus],
    oletus: "tila", toissijainen: { avain: "katsastus", suunta: 1 }, tyhjaEnsin: ["katsastus"], yksikko: t("kal.yksikko"), osoiteHaku, asetaOsoiteHaku,
  });
  const lataa = useCallback(async () => {
    try { setLista(await api.kalusto(naytaPoistetut)); setVirhe(null); }
    catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("pc.lataus_virhe")); setLista([]); }
  }, [naytaPoistetut]);
  const lataaTyypit = useCallback(async () => {
    try { setTyypit(await api.kalustoTyypit()); } catch { setTyypit([]); }
  }, []);
  useEffect(() => { void lataa(); }, [lataa]);
  useEffect(() => { void lataaTyypit(); }, [lataaTyypit]);
  useEffect(() => {
    if (!saaLuoda) return;
    api.kohteet().then((k) => setKohteet(koko ? k : k.filter((x) => omatKohteet.includes(Number(x.id))))).catch(() => setKohteet([]));
  }, [saaLuoda, koko]);
  const muuttui = async () => { await Promise.all([lataa(), lataaTyypit()]); };

  return (
    <>
      <header className="head">
        <div className="head__rivi">
          <h1>{t("valikko.kalusto")}</h1>
          {saaLuoda && (
            <button className="nappi nappi--paa" onClick={() => setUusiAuki((v) => !v)} data-avaa-lomake="kalusto">
              {uusiAuki ? t("hl.peruuta") : t("qr.uusi_laite")}
            </button>
          )}
        </div>
      </header>

      <Ohje
        kuka={<>{t("kal.ohje.kuka")}</>}
        luvut={<>{t("kal.ohje.luvut")}</>} />

      {koko && (
        <>
          <PohjaTuontiPaneeli
            otsikko={t("kal.tuonti_otsikko")}
            selite={t("kal.tuonti_selite")}
            pohjaUrl={api.kalustopohjaUrl()}
            tuo={(sisalto, tiedosto, vahvista, paivita) => api.tuoKalusto(sisalto, tiedosto, vahvista, paivita)}
            paivitysTeksti={t("kal.tuonti_paivita")}
            valmis={() => void muuttui()} />
        </>
      )}
    <section className="panel">
      {virhe && <p className="huom" role="alert">{virhe}</p>}
      {uusiAuki && (
        <LaiteLomake tyypit={tyypit} kohteet={kohteet} vainKohde={!koko}
          valmis={async () => { setUusiAuki(false); await muuttui(); }} />
      )}
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }} data-kalusto-laji="">
          <button type="button" className={"nappi " + (laji === "" ? "nappi--paa" : "nappi--kevyt")} aria-pressed={laji === ""} onClick={() => setLaji("")}>{t("kal.kaikki")}</button>
          {LAJIT.map((l) => (
            <button key={l.arvo} type="button" className={"nappi " + (laji === l.arvo ? "nappi--paa" : "nappi--kevyt")}
                    aria-pressed={laji === l.arvo} onClick={() => setLaji(l.arvo)}>
              {t(l.avain)} <span className="dim">{lista?.filter((k) => k.laji === l.arvo).length ?? ""}</span>
            </button>
          ))}
        </div>
        {saaLuoda && (
          <label className="dim" data-kalusto-odottavat={odottavat.length}>
            <input type="checkbox" checked={vainOdottavat} onChange={(e) => setVainOdottavat(e.target.checked)} />
            {" "}{t("kal.odottaa")} <strong>{odottavat.length}</strong>
          </label>
        )}
        <label className="dim">
          <input type="checkbox" checked={naytaPoistetut} onChange={(e) => setNaytaPoistetut(e.target.checked)} />
          {" "}{t("kal.nayta_poistetut")}
        </label>
        <a className="nappi nappi--kevyt" href={api.kalustoVientiUrl()} download data-kalusto-vienti="">{t("kal.vie_excel")}</a>
        {koko && (
          <button type="button" className="nappi nappi--kevyt" onClick={() => setTyyppilistaAuki((v) => !v)}>
            {tyyppilistaAuki ? t("kal.sulje_tyyppilista") : t("kal.tyyppilista")}
          </button>
        )}
      </div>
      {tyyppilistaAuki && koko && <Tyyppilista tyypit={tyypit} muuttui={lataaTyypit} />}

      {lista === null && <p className="dim">{t("yleinen.ladataan")}</p>}
      {nakyvat?.length === 0 && !uusiAuki && (
        /* Tyhjä tila ei lupaa toimintoa jota käyttäjä ei näe (Toni 24.9.2026):
           teksti seuraa samaa oikeutta kuin napit. */
        <p className="dim" data-tyhja-tila={koko ? "koko" : saaLuoda ? "kohde" : "ei-oikeutta"}>
          {laji ? t("kal.tyhja_laji") : t("kal.tyhja")}{" "}
          {koko
            ? t("kal.tyhja_koko")
            : saaLuoda
              ? t("kal.tyhja_kohde")
              : t("kal.tyhja_ei_oikeutta")}
        </p>
      )}

      {nakyvat && nakyvat.length > 0 && (
        <div className="taulukko__ohjaus" style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
          {tb.Haku({ placeholder: t("kal.haku") })}
          <span className="dim">{t("kal.jarjesta")}</span>
          {tb.Otsikko("tunnus", t("iv.tunnus"))}{tb.Otsikko("nimi", t("iv.nimi"))}{tb.Otsikko("tyyppi", t("pu.tyyppi"))}{tb.Otsikko("sijainti", t("qr.sijainti"))}{tb.Otsikko("tila", t("kal.s_tarkastus"))}{tb.Otsikko("katsastus", t("kal.s_seuraava"))}
          {tb.piilotettu > 0 && <span className="dim">{t("kal.piilossa", { n: tb.piilotettu })}</span>}
        </div>
      )}
      {/* 74: joukkohyväksyntä — napit listan yläpuolella, vahvistusrivi sivulla. */}
      {saaLuoda && odottavat.length > 0 && (
        <div className="allokointi joukko" data-kalusto-joukko="" style={{ marginBottom: 8 }}>
          <button type="button" className="nappi nappi--kevyt" disabled={joukkoKesken}
                  onClick={() => setValitut(odottavat.map((k) => k.id))} data-joukko-valitse-kaikki="">
            {t("kal.valitse_odottavat", { n: odottavat.length })}
          </button>
          <button type="button" className="nappi nappi--paa" disabled={joukkoKesken || valitutRivit.length === 0}
                  onClick={() => { setJoukkoVahvistus("hyvaksytty"); setJoukkoVirhe(null); }} data-joukko-hyvaksy="">
            {t("kal.hyvaksy_valitut", { n: valitutRivit.length })}
          </button>
          <button type="button" className="nappi nappi--kevyt" disabled={joukkoKesken || valitutRivit.length === 0}
                  onClick={() => { setJoukkoVahvistus("hylatty"); setJoukkoVirhe(null); }} data-joukko-hylkaa="">
            {t("kal.hylkaa_valitut")}
          </button>
          {/* 17.10.2026 (kävely kalustovastaavana): estetty nappi kertoo syynsä vieressä, kuten muualla (lomakekenttien selkeys). */}
          {!joukkoKesken && valitutRivit.length === 0 && <span className="este-syy" data-este-syy="">{t("kal.syy_valitse_rivit")}</span>}
          {valitut.length > 0 && <button type="button" className="nappi nappi--kevyt" onClick={() => { setValitut([]); setJoukkoVahvistus(null); }}>{t("kal.tyhjenna_valinta")}</button>}
          {joukkoVahvistus && valitutRivit.length > 0 && (
            <div className="huom" role="status" data-joukko-vahvistus={joukkoVahvistus} style={{ flexBasis: "100%" }}>
              <strong>{t(joukkoVahvistus === "hyvaksytty" ? "kal.hyvaksytaan" : "kal.hylataan", { n: valitutRivit.length })}</strong>{" "}
              {valitutRivit.map((k) => k.tunnus).join(", ")}
              {joukkoVahvistus === "hylatty" && (
                <label style={{ marginTop: 6 }}>{t("kal.joukko_peruste")}
                  <input type="text" size={40} value={joukkoPeruste} onChange={(e) => setJoukkoPeruste(e.target.value)} data-joukko-peruste="" /></label>
              )}
              <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                <button type="button" className="nappi nappi--paa" data-joukko-vahvista=""
                        disabled={joukkoKesken || (joukkoVahvistus === "hylatty" && joukkoPeruste.trim().length < 3)}
                        onClick={() => void joukkoPaata()}>{t("sel.vahvista")}</button>
                <button type="button" className="nappi nappi--kevyt" onClick={() => setJoukkoVahvistus(null)}>{t("qr.peru")}</button>
              </div>
            </div>
          )}
          {joukkoVirhe && <p className="huom huom--vika" role="alert" data-joukko-virhe="">{joukkoVirhe}</p>}
          {joukkoIlmoitus && <p className="huom" role="status" data-joukko-ilmoitus="">{joukkoIlmoitus}</p>}
        </div>
      )}
      {nakyvat && nakyvat.length > 0 && (
        <ul className="kalustolista">
          {tb.rivit.map((k) => (
            <KalustoKortti key={k.id} k={k} saaMuokata={saaRivia(k)} saaPoistaaLiite={saaRivia(k)} tyypit={tyypit} kohteet={kohteet} vainKohde={!koko}
              valittavissa={valittavissa(k)} valittu={valitut.includes(k.id)} vaihdaValinta={() => vaihdaValinta(k.id)}
              auki={avattu === k.id}
              avaa={() => setAvattu(avattu === k.id ? null : k.id)}
              muuttui={muuttui} />
          ))}
        </ul>
      )}
    </section>
    </>
  );
}

/* ── tyyppilista: nimen korjaus kulkee rekisteriin, piilotus poistaa valikosta ── */

function Tyyppilista({ tyypit, muuttui }: { tyypit: KalustoTyyppi[]; muuttui: () => Promise<void> }) {
  const [muokattava, setMuokattava] = useState<number | null>(null);
  const [nimi, setNimi] = useState("");
  const [ilmoitus, setIlmoitus] = useState<string | null>(null);
  async function tee(f: () => Promise<unknown>) {
    setIlmoitus(null);
    try { await f(); setMuokattava(null); await muuttui(); }
    catch (e) { setIlmoitus(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("kal.muutos_virhe")); }
  }
  return (
    <div className="alipaneeli" data-tyyppilista="">
      <p className="dim" style={{ marginTop: 0 }}>
        {t("kal.tyyppilista_ohje")}
      </p>
      {LAJIT.filter((l) => l.arvo !== "henkilovaljaat").map((l) => (
        <div key={l.arvo}>
          <h4>{t(l.avain)}</h4>
          <ul className="tyyppilista__rivit">
            {tyypit.filter((ty) => ty.laji === l.arvo).map((ty) => (
              <li key={ty.id} className="tyyppilista__rivi">
                {muokattava === ty.id ? (
                  <>
                    <input value={nimi} onChange={(e) => setNimi(e.target.value)} size={14} />
                    <button type="button" className="nappi nappi--kevyt" onClick={() => void tee(() => api.paivitaKalustoTyyppi(ty.id, { nimi }))}>{t("kuittaus.tallenna")}</button>
                  </>
                ) : (
                  <span className={ty.aktiivinen ? "" : "dim"}>{ty.nimi} <span className="dim">({ty.kaytossa})</span></span>
                )}
                {muokattava !== ty.id && (
                  <button type="button" className="nappi nappi--kevyt" onClick={() => { setMuokattava(ty.id); setNimi(ty.nimi); }}>{t("kal.korjaa")}</button>
                )}
                <button type="button" className="nappi nappi--kevyt"
                        onClick={() => void tee(() => api.paivitaKalustoTyyppi(ty.id, { aktiivinen: !ty.aktiivinen }))}>
                  {ty.aktiivinen ? t("kal.piilota") : t("kal.palauta")}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ))}
      {ilmoitus && <p className="huom" role="alert">{ilmoitus}</p>}
    </div>
  );
}

/* ── laitteen tiedot: sama lomake uudelle ja muokkaukselle ── */

function LaiteLomake({ tyypit, kohteet, vainKohde, laite, valmis }: {
  tyypit: KalustoTyyppi[]; kohteet: Kohde[]; vainKohde: boolean;
  laite?: KalustoRivi; valmis: () => Promise<void>;
}) {
  const [nimi, setNimi] = useState(laite?.nimi ?? "");
  const [laji, setLaji] = useState<KalustoLaji>(laite?.laji ?? "tyokalu");
  const [tyyppi, setTyyppi] = useState(laite?.tyyppi ?? "");
  const [uusiTyyppi, setUusiTyyppi] = useState("");
  const [kunto, setKunto] = useState<string>(laite?.kunto ?? "");
  const [kantavuus, setKantavuus] = useState(laite?.kantavuus ?? "");
  const [sLaji, setSLaji] = useState<string>(laite?.sijainti_laji ?? (vainKohde ? "kohde" : ""));
  const [sKohde, setSKohde] = useState<string>(laite?.sijainti_kohde_id != null ? String(laite.sijainti_kohde_id) : "");
  // T589 (arkkitehtuuri vaihe 1, kohdekoukku; Toni 16.10.2026 klo 03.45): oletus yläpalkin kohteesta (useKohde), vaihdettavissa.
  const { valittu: ylaKohde } = useKohde();
  useEffect(() => {
    if (laite || typeof ylaKohde !== "number") return;
    if (sKohde === "") setSKohde(String(ylaKohde));
    if (sLaji === "") setSLaji("kohde");   // kohdekenttä näkyy vain lajilla «kohde»; oletus ilman lajia jäisi piiloon
  }, [ylaKohde]);
  const [sTeksti, setSTeksti] = useState(laite?.sijainti_teksti ?? "");
  const [valmistaja, setValmistaja] = useState(laite?.valmistaja ?? "");
  const [huomiot, setHuomiot] = useState(laite?.huomiot ?? "");
  const [siirtoPeruste, setSiirtoPeruste] = useState("");
  const [virhe, setVirhe] = useState<string | null>(null);
  // Kenttä jonka takia tallennus estyi: virheväri ja syy vain sen kohdalla (24.9.2026).
  const [virheKentta, setVirheKentta] = useState<string | null>(null);
  const [tallennetaan, setTallennetaan] = useState(false);
  const estä = (kentta: string | null, viesti: string) => { setVirheKentta(kentta); setVirhe(viesti); };
  const vika = (kentta: string) => virheKentta === kentta;
  const syy = (kentta: string) => vika(kentta) && virhe ? <span className="kenttavirhe" data-kenttavirhe={kentta}>{virhe}</span> : null;

  // 165: pois käytöstä olevan laitteen sijainnin muutos kysyy perusteen.
  const siirtyy = !!laite?.pois_kaytosta && (
    (sLaji || null) !== (laite.sijainti_laji ?? null)
    || (sLaji === "kohde" ? Number(sKohde || 0) !== Number(laite.sijainti_kohde_id ?? 0)
                          : (sTeksti.trim() || null) !== (laite.sijainti_teksti ?? null)));
  const lajinTyypit = tyypit.filter((t) => t.laji === laji && (t.aktiivinen || t.nimi === laite?.tyyppi));
  const valjaat = laji === "henkilovaljaat";

  async function tallenna(e: React.FormEvent) {
    e.preventDefault();
    // Nimetyt syyt — nappi ei ole estetty, painallus kertoo mikä puuttuu (sääntö «väite painaa nappia»).
    if (!nimi.trim()) { estä("nimi", t("kal.v_nimi")); return; }
    if (tyyppi === MUU && !uusiTyyppi.trim()) { estä("uusi_tyyppi", t("kal.v_uusi_tyyppi")); return; }
    if (sLaji === "kohde" && !sKohde) { estä("sijainti_kohde_id", t("kal.v_kohde")); return; }
    if (sLaji === "auto" && !sTeksti.trim()) { estä("sijainti_teksti", t("kal.v_auto")); return; }
    if (siirtyy && !siirtoPeruste.trim()) { estä("siirto_peruste", t("kal.v_siirto")); return; }
    estä(null, ""); setVirhe(null); setTallennetaan(true);
    const syote: KalustoSyote = {
      laji,
      tyyppi: valjaat ? null : tyyppi === MUU ? uusiTyyppi.trim() : (tyyppi || null),
      lisaa_tyyppi: tyyppi === MUU,
      kunto: kunto || null,
      kantavuus: laji === "nostoapuvaline" ? (kantavuus.trim() || null) : null,
      sijainti_laji: (sLaji || null) as KalustoSijaintilaji | null,
      sijainti_kohde_id: sLaji === "kohde" ? Number(sKohde) : null,
      sijainti_teksti: sLaji && sLaji !== "kohde" ? (sTeksti.trim() || null) : null,
      valmistaja: valmistaja.trim() || null,
      huomiot: huomiot.trim() || null,
      ...(siirtyy ? { siirto_peruste: siirtoPeruste.trim() } : {}),
    };
    try {
      if (laite) await api.paivitaKalusto(laite.id, { ...syote, nimi: nimi.trim() });
      else await api.luoKalusto({ ...syote, nimi: nimi.trim(), laji });
      await valmis();
    } catch (e) {
      // Palvelimen syy kentän kohdalle, jos palvelin nimesi kentän (esim. tyyppi_tuntematon, tunnus_varattu).
      if (e instanceof ApiError) estä(e.kentta === "tyyppi" && tyyppi === MUU ? "uusi_tyyppi" : e.kentta ?? null, virheTeksti(e.virhe, e.message));
      else estä(null, t("ltar.tallennus_virhe"));
    } finally { setTallennetaan(false); }
  }

  return (
    <form className="lomake alipaneeli" onSubmit={(e) => void tallenna(e)} noValidate data-laitelomake="">
      <div className="allokointi">
        {/* 176: tunnus annetaan tallennettaessa seuraavana numerona — sitä ei kysytä eikä johdeta tarrasta. */}
        {!laite && <p className="dim" data-tunnus-automaattinen="">{t("kal.tunnus_auto")}</p>}
        <label>{t("qr.laitteen_nimi")}
          {/* 5.10.2026 (protot K-1): «Nimi» luettiin kentällä omaksi nimeksi — laite sai lisääjän nimen. */}
          <input value={nimi} onChange={(e) => setNimi(e.target.value)} size={22} autoFocus={!laite} aria-invalid={vika("nimi")} data-kentta="nimi"
                 placeholder={t("qr.laitteen_nimi_esim")} />{syy("nimi")}
        </label>
        <label>{t("qr.laji")}
          <select value={laji} onChange={(e) => { setLaji(e.target.value as KalustoLaji); setTyyppi(""); }} data-kentta="laji">
            {LAJIT.map((l) => <option key={l.arvo} value={l.arvo}>{nayttonimi("kalusto_laji", l.arvo)}</option>)}
          </select>
        </label>
        {!valjaat && (
          <label>{t("pu.tyyppi")}
            <select value={tyyppi} onChange={(e) => setTyyppi(e.target.value)} aria-invalid={vika("tyyppi")} data-kentta="tyyppi">
              <option value="">{t("qr.ei_valittu")}</option>
              {lajinTyypit.map((t) => <option key={t.id} value={t.nimi}>{t.nimi}</option>)}
              <option value={MUU}>{t("kal.muu_tyyppi")}</option>
            </select>{syy("tyyppi")}
          </label>
        )}
        {!valjaat && tyyppi === MUU && (
          <label>{t("kal.uusi_tyyppi")}
            <input value={uusiTyyppi} onChange={(e) => setUusiTyyppi(e.target.value)} size={16} aria-invalid={vika("uusi_tyyppi")} data-kentta="uusi_tyyppi" />{syy("uusi_tyyppi")}
          </label>
        )}
        {laji === "nostoapuvaline" && (
          <label>{t("kal.kantavuus")}
            <input value={kantavuus} onChange={(e) => setKantavuus(e.target.value)} size={8} placeholder={t("kal.kantavuus_esim")} aria-invalid={vika("kantavuus")} data-kentta="kantavuus" />{syy("kantavuus")}
          </label>
        )}
        <label>{t("qr.kunto")}
          <select value={kunto} onChange={(e) => setKunto(e.target.value)} data-kentta="kunto">
            <option value="">{t("kal.ei_arvioitu")}</option>
            {vaihtoehdot("kalusto_kunto").map((v) => <option key={v.arvo} value={v.arvo}>{v.nimi}</option>)}
          </select>
        </label>
        <label>{t("qr.sijainti")}
          <select value={sLaji} onChange={(e) => setSLaji(e.target.value)} data-kentta="sijainti_laji">
            {!vainKohde && <option value="">{t("qr.ei_tiedossa_viivat")}</option>}
            {vaihtoehdot("kalusto_sijainti").filter((v) => v.arvo !== "henkilo" && (!vainKohde || v.arvo === "kohde"))
              .map((v) => <option key={v.arvo} value={v.arvo}>{v.nimi}</option>)}
          </select>
        </label>
        {sLaji === "kohde" && (
          <label>{t("kuittaus.kohde")}
            <select value={sKohde} onChange={(e) => setSKohde(e.target.value)} aria-invalid={vika("sijainti_kohde_id")} data-kentta="sijainti_kohde_id" data-kohdekoukku="kalusto">
              <option value="">{t("kal.valitse")}</option>
              {kohteet.map((k) => <option key={k.id} value={k.id}>{k.tunnus} {k.nimi}</option>)}
            </select>{syy("sijainti_kohde_id")}
          </label>
        )}
        {sLaji && sLaji !== "kohde" && (
          <label>{sLaji === "auto" ? t("qr.rekisterinumero") : t("kal.nimi_valinnainen")}
            <input value={sTeksti} onChange={(e) => setSTeksti(e.target.value)} size={12} aria-invalid={vika("sijainti_teksti")} data-kentta="sijainti_teksti" />{syy("sijainti_teksti")}
          </label>
        )}
        {siirtyy && (
          <label>{t("kal.siirron_peruste")}
            <input value={siirtoPeruste} onChange={(e) => setSiirtoPeruste(e.target.value)} size={28} aria-invalid={vika("siirto_peruste")} data-kentta="siirto_peruste" />{syy("siirto_peruste")}
          </label>
        )}
        <label>{t("qr.valmistaja")}
          <input value={valmistaja} onChange={(e) => setValmistaja(e.target.value)} size={14} data-kentta="valmistaja" />
        </label>
        <label>{t("kal.huomautukset")}
          <input value={huomiot} onChange={(e) => setHuomiot(e.target.value)} size={24} data-kentta="huomiot" />
        </label>
        <button className="nappi nappi--paa" type="submit" disabled={tallennetaan}>
          {tallennetaan ? t("pu.tallennetaan") : laite ? t("kal.tallenna_muutokset") : t("kal.lisaa_laite")}
        </button>
      </div>
      {/* Yleinen syy vain kun mikään kenttä ei ole syy — muuten syy on kentän vieressä. */}
      {/* 5.10.2026 (protot K-1): virhe näkyi vain kentän vieressä, ja kenttää (sijainti «henkilo») ei ollut lomakkeella →
          tallennus epäonnistui hiljaa neljästi. Virhe näkyy AINA napin vieressä; kentän vieressä lisäksi jos kenttä on. */}
      {virhe && <p className="huom huom--vika" role="alert" data-kalusto-virhe={virheKentta ?? "yleinen"}>{virhe}</p>}
    </form>
  );
}

/* ── yksi laite ─────────────────────────────────────────── */

function KalustoKortti({
  k, saaMuokata, saaPoistaaLiite, tyypit, kohteet, vainKohde, auki, avaa, muuttui, valittavissa = false, valittu = false, vaihdaValinta,
}: {
  k: KalustoRivi; saaMuokata: boolean; saaPoistaaLiite: boolean;
  tyypit: KalustoTyyppi[]; kohteet: Kohde[]; vainKohde: boolean;
  auki: boolean; avaa: () => void; muuttui: () => Promise<void>;
  /** 74: valintaruutu vain odottavalla rivillä, johon on oikeus. */
  valittavissa?: boolean; valittu?: boolean; vaihdaValinta?: () => void;
}) {
  const [muokkaus, setMuokkaus] = useState(false);
  const [historia, setHistoria] = useState<KalustoTarkastus[] | null>(null);
  const [ilmoitus, setIlmoitus] = useState<string | null>(null);
  const [tallennetaan, setTallennetaan] = useState(false);
  const [poistoAuki, setPoistoAuki] = useState(false);
  const [poistoSyy, setPoistoSyy] = useState("myyty");
  const [poistoPeruste, setPoistoPeruste] = useState("");
  const [palautusPeruste, setPalautusPeruste] = useState("");
  const [hylkaysPeruste, setHylkaysPeruste] = useState("");
  async function paata(paatos: "hyvaksytty" | "hylatty") {
    setTallennetaan(true); setIlmoitus(null);
    try { await api.kalustoHyvaksynta(k.id, paatos, paatos === "hylatty" ? hylkaysPeruste.trim() : undefined); await muuttui(); }
    catch (e) { setIlmoitus(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("kal.paatos_virhe")); }
    finally { setTallennetaan(false); }
  }

  /* 116: poisto on tietue — kuka, milloin, miksi. Peruste pakollinen. */
  async function poistaInventaariosta() {
    if (poistoPeruste.trim().length < 3) { setIlmoitus(t("kal.poisto_peruste_puuttuu")); return; }
    setTallennetaan(true); setIlmoitus(null);
    try { await api.poistaKalusto(k.id, { syy: poistoSyy, peruste: poistoPeruste.trim() }); setPoistoAuki(false); await muuttui(); }
    catch (e) { setIlmoitus(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("kal.poisto_virhe")); }
    finally { setTallennetaan(false); }
  }
  async function palautaInventaarioon() {
    if (palautusPeruste.trim().length < 3) { setIlmoitus(t("kal.palautus_peruste_puuttuu")); return; }
    setTallennetaan(true); setIlmoitus(null);
    try { await api.palautaKalusto(k.id, { peruste: palautusPeruste.trim() }); await muuttui(); }
    catch (e) { setIlmoitus(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("qr.palautus_virhe")); }
    finally { setTallennetaan(false); }
  }

  const [paiva, setPaiva] = useState("");
  const [asti, setAsti] = useState("");
  const [tarkastaja, setTarkastaja] = useState("");
  // 165: tulos on nimenomainen valinta — ei oletusta, ettei hylätty katoa hyväksytyksi.
  const [tulos, setTulos] = useState<"" | "hyvaksytty" | "ehdollinen" | "hylatty">("");
  const [tulosSyy, setTulosSyy] = useState("");

  const haeHistoria = useCallback(async () => {
    try { setHistoria(await api.kalustonTarkastukset(k.id)); }
    catch { setHistoria([]); }
  }, [k.id]);
  useEffect(() => { if (auki && historia === null) void haeHistoria(); }, [auki, historia, haeHistoria]);

  async function lisaaKatsastus() {
    // Nimetyt syyt, nappi ei ole estetty (väite painaa nappia).
    if (!tulos) { setIlmoitus(t("kal.v_tulos")); return; }
    if (tulos !== "hylatty" && !asti) { setIlmoitus(t("kal.v_asti")); return; }
    if (tulos === "ehdollinen" && !tulosSyy.trim()) { setIlmoitus(t("kal.v_ehdollinen")); return; }
    if (paiva && asti && asti < paiva) { setIlmoitus(t("kal.v_ennen")); return; }
    setTallennetaan(true); setIlmoitus(null);
    try {
      await api.lisaaKatsastus(k.id, {
        tulos,
        ...(asti && tulos !== "hylatty" && { voimassa_asti: asti }),
        ...(paiva && { tarkastuspaiva: paiva }),
        ...(tarkastaja.trim() && { tarkastaja: tarkastaja.trim() }),
        ...(tulosSyy.trim() && { tulos_syy: tulosSyy.trim() }),
      });
      setPaiva(""); setAsti(""); setTarkastaja(""); setTulos(""); setTulosSyy("");
      await haeHistoria();
      await muuttui();
      setIlmoitus(tulos === "hylatty" ? t("kal.hylatty_kirjattu") : t("kal.tarkastus_kirjattu"));
    } catch (e) {
      setIlmoitus(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("ltar.tallennus_virhe"));
    } finally { setTallennetaan(false); }
  }

  async function poistaKatsastus(id: number) {
    setTallennetaan(true);
    try {
      await api.poistaKatsastus(id);
      await haeHistoria();
      await muuttui();
    } catch (e) {
      setIlmoitus(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("kal.poisto_virhe"));
    } finally { setTallennetaan(false); }
  }

  return (
    <li className={"kalusto" + (k.aktiivinen ? "" : " kalusto--pois") + (valittu ? " kalusto--valittu" : "")} data-kalusto-rivi={k.id} data-valittavissa={valittavissa ? "1" : "0"}>
      {valittavissa && (
        <label className="kalusto__valinta" title={t("kal.valitse_joukko")}>
          <input type="checkbox" checked={valittu} onChange={() => vaihdaValinta?.()} data-kalusto-valinta={k.id} aria-label={t("kal.valitse_rivi", { tunnus: k.tunnus })} />
        </label>
      )}
      <button className="kalusto__paa" onClick={avaa} aria-expanded={auki}>
        <span className="lyh">{k.tunnus}</span>
        {/* Korjaus b: sama numero kuin laitteessa ja skannauskortissa, sanan kanssa — «Tarra 17» ei ole tunnus. */}
        {k.tarra != null && <span className="dim mono" data-tarra={k.tarra}>{t("kal.tarra", { n: k.tarra })}</span>}
        <span className="kalusto__nimi">{k.nimi}</span>
        <span className="dim mono" data-laji={k.laji}>
          {k.tyyppi ?? nayttonimi("kalusto_laji", k.laji)}
          {k.kantavuus && <> · WLL {k.kantavuus}</>}
        </span>
        {k.sijainti_laji && <span className="dim" data-sijainti="">{sijaintiTeksti(k)}</span>}
        {k.kunto && k.kunto !== "hyva" && (
          <span className={k.kunto === "tyydyttava" ? "dim" : "ktila ktila--vanhenee"}>{nayttonimi("kalusto_kunto", k.kunto)}</span>
        )}
        {k.hyvaksynta === "ehdotettu" && <Tilasana laji="varoitus" data-kalusto-hyvaksynta="ehdotettu">{t("kal.ehdotettu_odottaa")}</Tilasana>}
        {k.hyvaksynta === "hylatty" && <Tilasana laji="vika" data-kalusto-hyvaksynta="hylatty">{t("kal.hylatty_pieni")}</Tilasana>}
        {k.poistettu
          ? <span className="dim" title={k.poisto_peruste ?? ""}>{t("kal.poistettu")} {naytaPvm(k.poistettu)} · {k.poistaja} · {nayttonimi("kalusto_poisto_syy", k.poisto_syy ?? "muu")}</span>
          : <span className={`ktila ktila--${k.tila}`}>
              {nayttonimi("kalusto_tila", k.tila)}
              {k.katsastus_asti && <span className="mono"> · {naytaPvm(k.katsastus_asti)}</span>}
            </span>}
      </button>

      {auki && (
        <div className="kalusto__laajennus">
          {saaMuokata && !k.poistettu && (
            muokkaus
              ? <LaiteLomake tyypit={tyypit} kohteet={kohteet} vainKohde={vainKohde} laite={k}
                  valmis={async () => { setMuokkaus(false); await muuttui(); }} />
              : <button type="button" className="nappi nappi--kevyt" onClick={() => setMuokkaus(true)}>{t("kal.muokkaa_tietoja")}</button>
          )}
          <p className="dim" style={{ margin: "6px 0" }}>
            {nayttonimi("kalusto_laji", k.laji)}
            {k.valmistaja && <> · {k.valmistaja}</>}{k.malli && <> {k.malli}</>}{k.sarjanumero && <> · {t("kal.sarjanro")} {k.sarjanumero}</>}
            {" · "}{k.kunto ? t("kal.kunto_arvo", { kunto: nayttonimiPieni("kalusto_kunto", k.kunto) }) : t("kal.kunto_ei")}
            {" · "}{k.sijainti_laji ? sijaintiTeksti(k) : t("kal.sijainti_ei")}
            {k.huomiot && <> · {k.huomiot}</>}
          </p>
          {/* 55: kalustovastaavan päätös kenttätekijän ehdotuksesta. Hylkäys vaatii perusteen; rivi ei katoa. */}
          {k.hyvaksynta === "ehdotettu" && (
            <div className="allokointi" data-kalusto-paatos="">
              <span className="dim">{t("qr.ehdotettu")} {naytaPvm(k.ehdotettu)}{k.ehdottaja ? ` · ${k.ehdottaja}` : ""}.</span>
              {saaMuokata ? (
                <>
                  <button className="nappi nappi--paa" disabled={tallennetaan} onClick={() => void paata("hyvaksytty")}>{t("kal.hyvaksy")}</button>
                  <label>{t("kal.hylkays_peruste")}
                    <input type="text" size={30} value={hylkaysPeruste} onChange={(e) => setHylkaysPeruste(e.target.value)} /></label>
                  <button className="nappi nappi--kevyt" disabled={tallennetaan || hylkaysPeruste.trim().length < 3} onClick={() => void paata("hylatty")}>{t("kal.hylkaa")}</button>
                </>
              ) : <span className="dim">{t("kal.paattaa_vastaava")}</span>}
            </div>
          )}
          {k.hyvaksynta === "hylatty" && (
            <p className="huom" data-kalusto-paatos="hylatty">{t("qr.hylatty")} {naytaPvm(k.hyvaksynta_hetki)}{k.hyvaksyja ? `, ${k.hyvaksyja}` : ""}: {k.hyvaksynta_peruste}</p>
          )}
          {/* 164: kuva laitteelle (Toni 22.9.). */}
          <Liitteet kohdeTaulu="kalusto" kohdeId={k.id} saaMuokata={saaMuokata} saaPoistaa={saaPoistaaLiite} otsikko={t("kal.kuva")} arkaluonteinenOletus={false} />
          {k.poistettu && (
            <p className="huom">
              {t("qr.poistettu")} {naytaPvm(k.poistettu)}, {k.poistaja}: <b>{nayttonimi("kalusto_poisto_syy", k.poisto_syy ?? "muu")}</b> — {k.poisto_peruste}
              {saaMuokata && (
                <span className="allokointi" style={{ marginTop: 8 }}>
                  <label>{t("kal.palautus_peruste_kentta")}
                    <input type="text" size={30} value={palautusPeruste} onChange={(e) => setPalautusPeruste(e.target.value)} /></label>
                  <button className="nappi nappi--kevyt" disabled={tallennetaan} onClick={() => void palautaInventaarioon()}>{t("kal.palauta_inventaarioon")}</button>
                </span>
              )}
            </p>
          )}
          {!k.poistettu && saaMuokata && (
            <div className="allokointi">
              {!poistoAuki && <button className="nappi nappi--kevyt" onClick={() => setPoistoAuki(true)}>{t("kal.poista_inventaariosta_auki")}</button>}
              {poistoAuki && (
                <>
                  <label>{t("qr.syy")}
                    <select value={poistoSyy} onChange={(e) => setPoistoSyy(e.target.value)}>
                      {vaihtoehdot("kalusto_poisto_syy").map((v) => <option key={v.arvo} value={v.arvo} title={v.selite}>{v.nimi}</option>)}
                    </select></label>
                  <label>{t("kal.peruste_pakollinen")}
                    <input type="text" size={36} value={poistoPeruste} placeholder={t("kal.poisto_esim")}
                           onChange={(e) => setPoistoPeruste(e.target.value)} /></label>
                  <button className="nappi nappi--paa" disabled={tallennetaan || poistoPeruste.trim().length < 3}
                          onClick={() => void poistaInventaariosta()}>{t("kal.poista_inventaariosta")}</button>
                  <button className="nappi nappi--kevyt" onClick={() => setPoistoAuki(false)}>{t("hl.peruuta")}</button>
                </>
              )}
            </div>
          )}
          {k.pois_kaytosta && (
            <p className="huom" data-pois-kaytosta="">
              <strong>{t("kal.pois_kaytosta")}</strong> {t("kal.pois_kaytosta_teksti")}
            </p>
          )}
          <h4>{t("kal.tarkastukset")}</h4>
          {historia === null && <p className="dim">{t("yleinen.ladataan")}</p>}
          {historia?.length === 0 && <p className="dim">{t("kal.ei_tarkastuksia")}</p>}
          {historia && historia.length > 0 && (
            <ul className="katsastukset">
              {historia.map((ta) => (
                <li key={ta.id} className="katsastus">
                  <div className="katsastus__rivi">
                    <span>
                      <span className={ta.tulos === "hylatty" ? "ktila ktila--pois_kaytosta" : ta.tulos === "ehdollinen" ? "ktila ktila--vanhenee" : ""}
                            data-tulos={ta.tulos} title={selite("katsastus_tulos", ta.tulos)}>
                        {nayttonimi("katsastus_tulos_valinta", ta.tulos)}
                      </span>
                      {ta.voimassa_asti && (() => { const [ennen, jalkeen] = t("kal.voimassa", { pvm: "\u0001" }).split("\u0001"); return <> · {ennen}<strong>{naytaPvm(ta.voimassa_asti)}</strong>{jalkeen}</>; })()}
                      {ta.tarkastuspaiva && <span className="dim"> {t("kal.tarkastettu", { pvm: naytaPvm(ta.tarkastuspaiva) ?? "" })}</span>}
                      {ta.tulos_syy && <span> · {ta.tulos_syy}</span>}
                      <span className="dim"> · {t("kal.kirjannut", { kuka: ta.kirjaaja ?? "—", pvm: naytaPvm(ta.luotu) ?? "" })}</span>
                    </span>
                    {ta.tarkastaja && <span className="mono dim">{ta.tarkastaja}</span>}
                    {saaMuokata && (
                      <button className="nappi nappi--kevyt" disabled={tallennetaan}
                              onClick={() => void poistaKatsastus(ta.id)}>{t("liite.poista")}</button>
                    )}
                  </div>
                  {/* Todistus kiinnittyy juuri tähän katsastukseen. */}
                  <Liitteet kohdeTaulu="kalusto_tarkastus" kohdeId={ta.id}
                            saaMuokata={saaMuokata} saaPoistaa={saaPoistaaLiite} otsikko={t("kal.todistus")} />
                </li>
              ))}
            </ul>
          )}

          {saaMuokata && (
            <div className="allokointi">
              <label>
                {t("iv.tulos")}
                <select value={tulos} onChange={(e) => setTulos(e.target.value as typeof tulos)} data-kentta="tulos">
                  <option value="">{t("kal.valitse")}</option>
                  {vaihtoehdot("katsastus_tulos_valinta").map((v) => <option key={v.arvo} value={v.arvo} title={v.selite}>{v.nimi}</option>)}
                </select>
              </label>
              <label>
                {t("kal.tarkastuspaiva")}
                <input type="date" value={paiva} onChange={(e) => setPaiva(e.target.value)} />
              </label>
              {tulos !== "hylatty" && (
                <label>
                  {t("kal.seuraava_tarkastus")}
                  <input type="date" value={asti} onChange={(e) => setAsti(e.target.value)} data-kentta="voimassa_asti" />
                </label>
              )}
              {(tulos === "ehdollinen" || tulos === "hylatty") && (
                <label>
                  {tulos === "ehdollinen" ? t("kal.syy_pakollinen") : t("qr.syy")}
                  <input type="text" size={24} value={tulosSyy} onChange={(e) => setTulosSyy(e.target.value)} data-kentta="tulos_syy" />
                </label>
              )}
              <label>
                {t("ltar.tarkastaja")}
                <input type="text" size={12} value={tarkastaja}
                       onChange={(e) => setTarkastaja(e.target.value)} />
              </label>
              <button className="nappi nappi--paa" disabled={tallennetaan}
                      onClick={() => void lisaaKatsastus()} data-kirjaa-tarkastus="">
                {t("qr.kirjaa_tarkastus")}
              </button>
            </div>
          )}

          {ilmoitus && <p className="huom" role="status">{ilmoitus}</p>}
        </div>
      )}
    </li>
  );
}
